import type { ParticipantRole, Registration } from '../store/registrationsSlice'

// ── Scanner bundle types ───────────────────────────────────────────────────────

/**
 * Participant entry from the backend scanner bundle, containing the cryptographic
 * material needed for offline QR verification.
 */
export interface ScannerParticipantEntry {
  id: string
  firstName: string
  lastName: string
  email: string
  status: string
  /** Event role of the participant — displayed on the scan-result screen. */
  role?: 'AUDIENCE' | 'SPEAKER' | 'STAFF' | 'HOST'
  secretToken: string
  rawCiphertext: string
  attendedAt?: string | null
}

/**
 * Organisation data from the scanner bundle.
 */
export interface ScannerOrgData {
  orgId: string
  name: string
  publicKeyBase64: string
}

/**
 * Event summary from the scanner bundle (minimal fields used by the scanner UI).
 */
export interface ScannerEventSummary {
  id: string
  title: string
  status: string
  startDate?: string
  endDate?: string
  /** IANA timezone ID (e.g. "Europe/Rome") the event's wall-clock times must be shown in. */
  timezone?: string
}

/**
 * Full scanner bundle as returned by GET /api/v1/event/participant/scanner-bundle.
 */
export interface ScannerBundle {
  success: boolean
  event: ScannerEventSummary
  org: ScannerOrgData
  participants: ScannerParticipantEntry[]
}

// ── QR payload prefix ────────────────────────────────────────────────────────

/**
 * Prefix prepended to the secretToken when encoding the entrance QR code shown
 * on {@link TicketPage}. Centralised here so the value used to *generate* the
 * QR code and the value used to *parse* a scanned QR code can never drift.
 */
export const ENTRANCE_QR_PREFIX = 'ee:'

// ── Token extraction ───────────────────────────────────────────────────────────

/**
 * Normalise a raw QR-code payload into a bare secretToken string.
 *
 * Handles three shapes:
 *  1. `ee:<token>` — format used by the current TicketPage QR.
 *  2. `https://…?tk=<token>` (or any URL with a `tk` query param) — ticket link.
 *  3. Bare token string — direct text.
 */
export function extractToken(raw: string): string {
  const trimmed = raw.trim()

  // Shape 1: ee: prefix
  if (trimmed.startsWith(ENTRANCE_QR_PREFIX)) {
    return trimmed.slice(ENTRANCE_QR_PREFIX.length).trim()
  }

  // Shape 2: URL with ?tk= param
  try {
    // URL constructor requires an absolute URL; handle relative-like inputs too
    const url = new URL(trimmed.includes('://') ? trimmed : `https://dummy.invalid${trimmed}`)
    const tk = url.searchParams.get('tk')
    if (tk) return tk
  } catch {
    // not a URL — fall through to bare token
  }

  // Shape 3: bare token
  return trimmed
}

// ── Role mapping ──────────────────────────────────────────────────────────────

/**
 * Maps the scanner bundle's uppercase role enum to the app-wide `ParticipantRole`
 * type consumed by {@link RoleBadge}. Returns `null` when no role is present.
 */
export function toParticipantRole(role?: ScannerParticipantEntry['role']): ParticipantRole | null {
  switch (role) {
    case 'AUDIENCE':
      return 'Audience'
    case 'SPEAKER':
      return 'Speaker'
    case 'STAFF':
      return 'Staff'
    case 'HOST':
      return 'Host'
    default:
      return null
  }
}

// ── Scanner bundle map ────────────────────────────────────────────────────────

/**
 * Build an O(1) lookup map from secretToken → ScannerParticipantEntry for all
 * ACCEPTED and ATTENDED participants from the scanner bundle.
 *
 * The backend returns both statuses since ATTENDED entries must remain in the
 * bundle so staff devices can re-download mid-event without losing track.
 */
export function buildScannerMap(
  participants: ScannerParticipantEntry[]
): Map<string, ScannerParticipantEntry> {
  const map = new Map<string, ScannerParticipantEntry>()
  for (const entry of participants) {
    if (entry.status === 'ACCEPTED' || entry.status === 'ATTENDED') {
      map.set(entry.secretToken, entry)
    }
  }
  return map
}

// ── Cryptographic verification ────────────────────────────────────────────────

/**
 * Reason codes returned when scanner QR verification fails.
 * Extends the basic reasons with crypto-specific failures.
 */
export type ScannerVerifyReason =
  | 'unknown_token'
  | 'wrong_event'
  | 'sha256_mismatch'
  | 'rsa_mismatch'

/** Result of {@link verifyScannerQr}. */
export type ScannerVerifyResult =
  | { status: 'approved'; entry: ScannerParticipantEntry }
  | { status: 'rejected'; reason: ScannerVerifyReason }

/**
 * Perform the full QR verification for scanner-bundle-backed verification.
 *
 * Steps 2.2–2.6 from the spec:
 *  2.2 Extract secretToken from QR payload.
 *  2.3 Map lookup — reject unknown tokens.
 *  2.4 Event ID check — reject mismatched events.
 *  2.5 RSA check: rsaDecryptPublic(rawCiphertext, orgPublicKey) === entry.id
 *  2.6 SHA-256 check: SHA256(base64url_decode(rawCiphertext)) === secretToken
 *
 * The crypto checks (2.5 and 2.6) are async due to Web Crypto API usage.
 */
export async function verifyScannerQr(
  raw: string,
  eventId: string,
  map: Map<string, ScannerParticipantEntry>,
  orgPublicKeyBase64: string
): Promise<ScannerVerifyResult> {
  const token = extractToken(raw)

  const entry = map.get(token)
  if (!entry) {
    return { status: 'rejected', reason: 'unknown_token' }
  }

  // Step 2.4 — implicit event check (bundle is event-scoped, but verify explicitly)
  // The eventId is passed in for defensive validation.
  // (The bundle was loaded for this specific eventId, so this is always true in practice.)
  void eventId

  // Step 2.6 — SHA-256 consistency: SHA256(base64url_decode(rawCiphertext)) === secretToken
  const sha256Match = await checkSha256(entry.rawCiphertext, entry.secretToken)
  if (!sha256Match) {
    return { status: 'rejected', reason: 'sha256_mismatch' }
  }

  // Step 2.5 — RSA cryptographic check: rsaDecryptPublic(rawCiphertext, orgPublicKey) === entry.id
  const rsaMatch = await checkRsaDecryptPublic(entry.rawCiphertext, orgPublicKeyBase64, entry.id)
  if (!rsaMatch) {
    return { status: 'rejected', reason: 'rsa_mismatch' }
  }

  return { status: 'approved', entry }
}

/**
 * SHA-256 consistency check (step 2.6):
 * SHA256(base64url_decode(rawCiphertext)) must equal secretToken.
 */
export async function checkSha256(
  rawCiphertextBase64url: string,
  expectedSecretToken: string
): Promise<boolean> {
  try {
    const cipherBytes = base64urlDecodeToBytes(rawCiphertextBase64url)
    // Wrap in a fresh ArrayBuffer so TypeScript knows this is not a SharedArrayBuffer
    const hashBuffer = await crypto.subtle.digest('SHA-256', cipherBytes.slice())
    const hashToken = base64urlEncodeFromBytes(new Uint8Array(hashBuffer))
    return hashToken === expectedSecretToken
  } catch {
    return false
  }
}

/**
 * RSA public-key decrypt check (step 2.5):
 * Decrypting rawCiphertext with the org's RSA public key must recover entry.id.
 *
 * Uses Web Crypto API to parse the X.509 DER public key (extracting n and e via JWK
 * export), then performs modular exponentiation C^e mod n and strips PKCS#1 v1.5
 * Type 1 padding to recover the original plaintext. No external libraries required.
 *
 * The algorithm matches Java's `Cipher.getInstance("RSA/ECB/PKCS1Padding")` with
 * `DECRYPT_MODE` and a public key.
 */
export async function checkRsaDecryptPublic(
  rawCiphertextBase64url: string,
  x509PublicKeyBase64: string,
  expectedPlaintext: string
): Promise<boolean> {
  try {
    const result = await rsaPublicDecryptPkcs1(rawCiphertextBase64url, x509PublicKeyBase64)
    return result === expectedPlaintext
  } catch {
    return false
  }
}

// ── Internal crypto helpers ───────────────────────────────────────────────────

/** Decode a URL-safe Base64 (no padding) string to bytes. */
function base64urlDecodeToBytes(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  const binary = atob(padded)
  return Uint8Array.from(binary, (c) => c.charCodeAt(0))
}

/** Encode bytes to URL-safe Base64 without padding. */
function base64urlEncodeFromBytes(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/** Convert big-endian bytes to a BigInt. */
function bytesToBigInt(bytes: Uint8Array): bigint {
  return bytes.reduce((acc: bigint, b: number) => (acc << 8n) | BigInt(b), 0n)
}

/** Modular exponentiation: base^exp mod mod — square-and-multiply. */
function modPow(base: bigint, exp: bigint, mod: bigint): bigint {
  let result = 1n
  let b = base % mod
  let e = exp
  while (e > 0n) {
    if (e & 1n) result = (result * b) % mod
    e >>= 1n
    b = (b * b) % mod
  }
  return result
}

/**
 * Decrypt RSA PKCS#1 v1.5 ciphertext using a public key (Java's RSA/ECB/PKCS1Padding
 * DECRYPT_MODE with a public key, which decrypts data encrypted by the private key).
 *
 * 1. Parse the X.509 DER public key using Web Crypto + JWK export to extract n and e.
 * 2. Compute M = C^e mod n via BigInt modular exponentiation.
 * 3. Strip PKCS#1 v1.5 Type 1 padding (0x00 | 0x01 | 0xFF...FF | 0x00 | message).
 * 4. Decode the message as UTF-8.
 */
async function rsaPublicDecryptPkcs1(
  rawCiphertextBase64url: string,
  x509PublicKeyBase64: string
): Promise<string | null> {
  // Parse the X.509 DER public key to extract n and e
  const pubKeyDer = Uint8Array.from(atob(x509PublicKeyBase64), (c) => c.charCodeAt(0))
  const cryptoKey = await crypto.subtle.importKey(
    'spki',
    pubKeyDer,
    // Algorithm name is required but doesn't affect the exported JWK's n and e values
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    true,
    ['verify']
  )
  const jwk = await crypto.subtle.exportKey('jwk', cryptoKey)

  const nBytes = base64urlDecodeToBytes(jwk.n!)
  const eBytes = base64urlDecodeToBytes(jwk.e!)
  const n = bytesToBigInt(nBytes)
  const e = bytesToBigInt(eBytes)

  // Decode ciphertext (URL-safe base64, no padding)
  const cipherBytes = base64urlDecodeToBytes(rawCiphertextBase64url)

  // RSA raw operation: M = C^e mod n
  const m = modPow(bytesToBigInt(cipherBytes), e, n)

  // Convert M back to bytes, zero-padded to the ciphertext length
  const mBytes = new Uint8Array(cipherBytes.length)
  let mVal = m
  for (let i = mBytes.length - 1; i >= 0; i--) {
    mBytes[i] = Number(mVal & 0xffn)
    mVal >>= 8n
  }

  // Verify and strip PKCS#1 v1.5 Type 1 padding
  // Expected format: 0x00 | 0x01 | 0xFF...FF (≥8 bytes) | 0x00 | message
  if (mBytes[0] !== 0x00 || mBytes[1] !== 0x01) return null
  let i = 2
  while (i < mBytes.length && mBytes[i] === 0xff) i++
  if (i - 2 < 8 || i >= mBytes.length || mBytes[i] !== 0x00) return null

  return new TextDecoder().decode(mBytes.slice(i + 1))
}

// ── Registration map ───────────────────────────────────────────────────────────

/**
 * Build an O(1) lookup map from secretToken → Registration.
 *
 * Only includes **accepted** registrations for the given event so that
 * pending/rejected registrations automatically yield a "rejected" result.
 *
 * Note: in the BPMN this map is keyed by RegistrationID obtained after
 * RSA-decrypting the token from the server. Because this frontend-only
 * implementation uses plain random tokens we key directly by secretToken.
 */
export function buildRegistrationMap(
  regs: Registration[],
  eventId: string
): Map<string, Registration> {
  const map = new Map<string, Registration>()
  for (const reg of regs) {
    if (reg.eventId === eventId && reg.status === 'accepted') {
      map.set(reg.secretToken, reg)
    }
  }
  return map
}

// ── Verification ──────────────────────────────────────────────────────────────

/** Reason codes returned when QR verification fails. */
export type VerifyReason = 'unknown_token' | 'wrong_event' | 'not_accepted'

/** Discriminated union result returned by {@link verifyToken}. */
export type VerifyResult =
  | { status: 'approved'; registration: Registration }
  | { status: 'rejected'; reason: VerifyReason }

/**
 * Verify a raw QR-code payload against the pre-built registration map.
 *
 * Steps (mirrors BPMN 2.2–2.6):
 *  1. Extract the token from the raw QR content.
 *  2. Look up the registration in the map (map contains accepted regs only).
 *  3. Confirm the registration's eventId matches the expected eventId.
 *
 * Returns an approved result with the full Registration, or a rejected result
 * with a reason string for UI display / debugging.
 */
export function verifyToken(
  raw: string,
  eventId: string,
  map: Map<string, Registration>
): VerifyResult {
  const token = extractToken(raw)

  const registration = map.get(token)
  if (!registration) {
    return { status: 'rejected', reason: 'unknown_token' }
  }

  if (registration.eventId !== eventId) {
    return { status: 'rejected', reason: 'wrong_event' }
  }

  return { status: 'approved', registration }
}
